import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { isBoard as checkIsBoard, isTrainer, isGuest, isMember, getRolesDisplay } from '../utils/roleUtils';
import apiClient from '../utils/apiClient';
import packageJson from '../../package.json';
import BoardNotesPanel from './BoardNotesPanel';
import PraesenzAnzeige from './PraesenzAnzeige';
import UserActivityTracker from './UserActivityTracker';
import {
  Bars3Icon,
  XMarkIcon,
  CalendarIcon,
  UserGroupIcon,
  CurrencyEuroIcon,
  HomeIcon,
  UserCircleIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  CogIcon,
  ShieldCheckIcon,
  FolderIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  CreditCardIcon,
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  TicketIcon,
  AcademicCapIcon,
  SparklesIcon,
  CloudArrowDownIcon,
  ArchiveBoxIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { FaPaypal, FaDog } from 'react-icons/fa';

// Create a typed icon component for PayPal
const PayPalIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaPaypal as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

// Create a typed icon component for Dog
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

// Page titles for mobile header with icons
const pageTitles: Record<string, { title: string; subtitle?: string; icon: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Übersicht', icon: 'home' },
  '/calendar': { title: 'Kalender', subtitle: 'Termine & Events', icon: 'calendar' },
  '/my-bookings': { title: 'Meine Buchungen', subtitle: 'Deine Termine', icon: 'calendar' },
  '/my-drinks': { title: 'Verzehrkosten', subtitle: 'Deine Getränkebeiträge', icon: 'cart' },
  '/my-dogs': { title: 'Meine Hunde', subtitle: 'Deine Vierbeiner', icon: 'dog' },
  '/my-guest-cards': { title: 'Gästekarte', subtitle: 'Deine Gästekarte', icon: 'card' },
  '/profile': { title: 'Mein Profil', subtitle: 'Deine Daten', icon: 'user' },
  '/my-projects': { title: 'Vereinsprojekte', subtitle: 'Aktive Projekte', icon: 'folder' },
  '/veranstaltungen': { title: 'Veranstaltungen', subtitle: 'Mein Verein', icon: 'ticket' },
  '/admin/events': { title: 'Veranstaltungen', subtitle: 'Verwaltung', icon: 'ticket' },
  '/members': { title: 'Mitglieder', subtitle: 'Verwaltung', icon: 'users' },
  '/dog-management-beta': { title: 'Hunde', subtitle: 'Verwaltung', icon: 'dog' },
  '/guest-cards': { title: 'Gästekarten', subtitle: 'Verwaltung', icon: 'card' },
  '/verzehrkosten': { title: 'Verzehrkosten', subtitle: 'Verwaltung', icon: 'cart' },
  '/admin/projects': { title: 'Projekte', subtitle: 'Verwaltung', icon: 'folder' },
  '/admin/waitlist': { title: 'Warteliste', subtitle: 'Verwaltung', icon: 'clipboard' },
  '/account-overview': { title: 'Finanzen', subtitle: 'Übersicht', icon: 'chart' },
  '/account-statements': { title: 'Kontoauszüge', subtitle: 'Finanzen', icon: 'document' },
  '/budget-planning': { title: 'Planung', subtitle: 'Finanzen', icon: 'euro' },
  '/paypal-payments': { title: 'PayPal', subtitle: 'Zahlungen', icon: 'paypal' },
  '/documents': { title: 'Dokumente', subtitle: 'Dateien', icon: 'folder' },
  '/member-statistics': { title: 'Statistik', subtitle: 'Auswertungen', icon: 'chart' },
  '/admin/config': { title: 'Einstellungen', subtitle: 'Admin', icon: 'cog' },
  '/admin/kassenpruefer': { title: 'Kassenprüfer-Verwaltung', subtitle: 'Admin', icon: 'shield' },
  '/admin/structure': { title: 'Struktur', subtitle: 'Admin', icon: 'users' },
  '/admin/backup-export': { title: 'Backup & Export', subtitle: 'Admin', icon: 'cloud' },
  '/admin/backup-status': { title: 'Backup-Status', subtitle: 'Admin', icon: 'cloud' },
  '/admin/user-activity': { title: 'Nutzungsanalyse', subtitle: 'Nur Vorstand', icon: 'chart' },
  '/admin/anlagen': { title: 'Anlagen', subtitle: 'Finanzen', icon: 'cloud' },
  '/admin/email-protokoll': { title: 'E-Mail-Protokoll', subtitle: 'Admin', icon: 'envelope' },
  '/admin/paypal-setup': { title: 'PayPal-Setup', subtitle: 'Admin', icon: 'paypal' },
  '/admin/privacy': { title: 'DSGVO', subtitle: 'Admin', icon: 'shield' },
  '/admin/components-demo': { title: 'Design System', subtitle: 'Demo', icon: 'sparkles' },
  '/kassenpruefer': { title: 'Kassenprüfer', subtitle: 'Finanzen', icon: 'shield' },
};

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [hasActiveProjects, setHasActiveProjects] = useState(false);
  const [hasUpcomingEvents, setHasUpcomingEvents] = useState(false);
  const [isKassenpruefer, setIsKassenpruefer] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [unreadNotesCount, setUnreadNotesCount] = useState(0);
  const [backendVersion, setBackendVersion] = useState<string>('');
  const { user, logout, darkMode, toggleDarkMode, roleDebugEnabled, debugRole, setDebugRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const clubName = localStorage.getItem('club_name') || 'Hundesportverein';

  // Get current page title
  const currentPage = pageTitles[location.pathname] || { title: clubName, icon: 'home' };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch backend version
  useEffect(() => {
    apiClient.get('/api/version')
      .then(res => { if (res.data?.version) setBackendVersion(res.data.version); })
      .catch(() => {});
  }, []);

  // Veranstaltungen nur anzeigen, wenn mindestens ein aktiver Termin noch nicht beendet ist.
  useEffect(() => {
    const checkUpcomingEvents = async () => {
      if (!user || isGuest(user)) {
        setHasUpcomingEvents(false);
        return;
      }

      try {
        const response = await apiClient.get('/api/veranstaltungen/admin/has-active');
        setHasUpcomingEvents(response.data?.success === true && response.data?.hasActive === true);
      } catch (error) {
        setHasUpcomingEvents(false);
      }
    };

    checkUpcomingEvents();
  }, [user, location.pathname]);

  // Check if there are active projects for members
  useEffect(() => {
    const checkActiveProjects = async () => {
      try {
        const response = await apiClient.get('/api/projects');
        if (response.data.success) {
          const activeProjects = response.data.projects.filter((p: any) => p.status === 'active');
          setHasActiveProjects(activeProjects.length > 0);
        }
      } catch (error) {
        // Gracefully handle error - projects feature might not be available yet
        // No need to show error to user, just keep hasActiveProjects as false
        setHasActiveProjects(false);
      }
    };

    checkActiveProjects();
  }, []);

  // Kassenprüfer: hat das Mitglied Zuweisungen? (treibt den Navigations-Reiter)
  useEffect(() => {
    const checkKassenpruefer = async () => {
      if (!user || isGuest(user)) {
        setIsKassenpruefer(false);
        return;
      }
      try {
        const response = await apiClient.get('/api/kassenpruefung/my-assignments');
        if (response.data.success) {
          setIsKassenpruefer((response.data.assignments || []).length > 0);
        }
      } catch (error) {
        setIsKassenpruefer(false);
      }
    };

    checkKassenpruefer();
  }, [user]);

  // Board Notes: Ungelesene Nachrichten polling
  const fetchUnreadCount = useCallback(async () => {
    if (!checkIsBoard(user)) return;
    try {
      const res = await apiClient.get('/api/board-notes/unread-count');
      setUnreadNotesCount(res.data?.count || 0);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const isBoardMember = checkIsBoard(user);
  const isTrainerUser = isTrainer(user);
  const isGuestUser = isGuest(user);
  const isMemberUser = isMember(user);

  // Determine which sections to show based on role
  const showDashboard = isBoardMember; // Dashboard only for board members
  const showMemberSection = isBoardMember;
  const showFinanceSection = isBoardMember;
  const showAdminSection = isBoardMember;
  const showKassenpruefer = isKassenpruefer; // Reiter „Kassenprüfer" nur für zugewiesene Prüfer
  const showGuestCards = isGuestUser;
  const showMyBookings = isBoardMember || isTrainerUser || isMemberUser; // Board, Trainers and Members see Meine Buchungen
  const showDrinks = (isBoardMember || isTrainerUser || isMemberUser) && !isGuestUser; // Board, Trainers and Members see Verzehrkosten, but not Guests

  return (
    <div className="app-shell min-h-screen bg-gray-50 dark:bg-background-dark">
      <UserActivityTracker />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`app-sidebar fixed inset-y-0 left-0 z-50 w-72 bg-[#fbfcfa] dark:bg-surface-dark border-r border-gray-200/80 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Sidebar Header */}
          <div className="flex h-[4.75rem] items-center justify-between px-4 border-b border-gray-200/80 dark:border-gray-700">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="h-11 w-11 flex-shrink-0 overflow-hidden rounded-full bg-primary-500 ring-1 ring-primary-600/20 shadow-sm">
                <img src="/HSV_Logo.jpg" alt="HSV Berlin-Adlershof" className="h-full w-full object-cover" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-sm font-bold tracking-tight text-gray-900 dark:text-white">
                  HSV Berlin-Adlershof e. V.
                </h1>
                <p className="mt-0.5 text-[11px] font-medium tracking-wide text-gray-500 dark:text-gray-400">
                  MITGLIEDERPORTAL
                </p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden h-10 w-10 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              aria-label="Navigation schließen"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="app-navigation flex-1 px-3 py-3 pb-20 overflow-y-auto">
            <div className="space-y-0.5">
            {showDashboard && (
              <NavLink
                to="/dashboard"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <HomeIcon className="h-5 w-5 mr-3" />
                Dashboard
              </NavLink>
            )}

            <NavLink
              to="/calendar"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <CalendarIcon className="h-5 w-5 mr-3" />
              Kalender
            </NavLink>

            {showMyBookings && (
              <NavLink
                to="/my-bookings"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <CalendarIcon className="h-5 w-5 mr-3" />
                Meine Buchungen
              </NavLink>
            )}

            {showDrinks && (
              <NavLink
                to="/my-drinks"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <ShoppingCartIcon className="h-5 w-5 mr-3" />
                Meine Verzehrkosten
              </NavLink>
            )}
            
            <NavLink
              to="/my-dogs"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <DogIcon className="h-5 w-5 mr-3" />
              Meine Hunde
            </NavLink>
            
            {showGuestCards && (
              <NavLink
                to="/my-guest-cards"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <CreditCardIcon className="h-5 w-5 mr-3" />
                Meine Gästekarte
              </NavLink>
            )}
            
            <NavLink
              to="/profile"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <UserCircleIcon className="h-5 w-5 mr-3" />
              Mein Profil
            </NavLink>

            {/* Persönlicher Zugang für Mitglieder mit Kassenprüfungs-Zuweisung */}
            {showKassenpruefer && (
              <NavLink
                to="/kassenpruefer"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <ShieldCheckIcon className="h-5 w-5 mr-3" />
                Kassenprüfer
              </NavLink>
            )}

            {/* Show Vereinsprojekte only if there are active projects and user is not a guest */}
            {hasActiveProjects && !isGuestUser && (
              <NavLink
                to="/my-projects"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <FolderIcon className="h-5 w-5 mr-3" />
                Vereinsprojekte
              </NavLink>
            )}

            {/* Veranstaltungen nur zeigen, wenn noch nicht abgelaufene Events vorhanden sind */}
            {hasUpcomingEvents && !isGuestUser && (
              <NavLink
                to="/veranstaltungen"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <TicketIcon className="h-5 w-5 mr-3" />
                Veranstaltungen
              </NavLink>
            )}

            {/* VERWALTUNG Section - Only for Board */}
            {showMemberSection && (
              <div className="pt-5 mt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="px-3 pb-2 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.18em]">
                  VERWALTUNG
                </h3>
                <div className="space-y-1">
                  <NavLink
                    to="/members"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <UserGroupIcon className="h-5 w-5 mr-3" />
                    Mitglieder
                  </NavLink>

                  <NavLink
                    to="/dog-management-beta"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <DogIcon className="h-5 w-5 mr-3" />
                    Hunde
                  </NavLink>
                  <NavLink
                    to="/guest-cards"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <CreditCardIcon className="h-5 w-5 mr-3" />
                    Gästekarten
                  </NavLink>
                  <NavLink
                    to="/verzehrkosten"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <ShoppingCartIcon className="h-5 w-5 mr-3" />
                    Verzehrkosten
                  </NavLink>
                  <NavLink
                    to="/admin/projects"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <FolderIcon className="h-5 w-5 mr-3" />
                    Projektverwaltung
                  </NavLink>
                  <NavLink
                    to="/admin/waitlist"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <ClipboardDocumentListIcon className="h-5 w-5 mr-3" />
                    Warteliste
                  </NavLink>
                  <NavLink
                    to="/admin/events"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <TicketIcon className="h-5 w-5 mr-3" />
                    Veranstaltungen
                  </NavLink>
                </div>
              </div>
            )}

            {/* Finance Section - Only for Board */}
            {showFinanceSection && (
              <div className="pt-1">
                <button
                  onClick={() => setFinanceOpen(!financeOpen)}
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center">
                    <CurrencyEuroIcon className="h-5 w-5 mr-3" />
                    Finanzen
                  </div>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${
                      financeOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {financeOpen && (
                  <div className="space-y-1 pl-11">
                    <NavLink
                      to="/account-overview"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <ChartBarIcon className="h-5 w-5 mr-3" />
                      Finanzübersicht
                    </NavLink>
                    <NavLink
                      to="/account-statements"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <DocumentTextIcon className="h-5 w-5 mr-3" />
                      Kontoauszüge
                    </NavLink>
                    <NavLink
                      to="/budget-planning"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <CurrencyEuroIcon className="h-5 w-5 mr-3" />
                      Planung
                    </NavLink>
                    <NavLink
                      to="/paypal-payments"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <PayPalIcon className="h-5 w-5 mr-3" />
                      Paypal
                    </NavLink>
                    {/*
                      Anlagen gehört sachlich zu den Finanzen: das Verzeichnis
                      speist die Vermögensaufstellung im Finanzamt-Export, nicht
                      die Administration der Anwendung.
                    */}
                    <NavLink
                      to="/admin/anlagen"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <ArchiveBoxIcon className="h-5 w-5 mr-3" />
                      Anlagen
                    </NavLink>
                  </div>
                )}
              </div>
            )}

            {/* Dokumente & Statistik Section - Only for Board */}
            {showAdminSection && (
              <div className="pt-1">
                <div className="space-y-1">
                  <NavLink
                    to="/documents"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <FolderIcon className="h-5 w-5 mr-3" />
                    Dokumente
                  </NavLink>

                  <NavLink
                    to="/member-statistics"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <ChartBarIcon className="h-5 w-5 mr-3" />
                    Statistik
                  </NavLink>

                  <NavLink
                    to="/ki-nutzung"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <SparklesIcon className="h-5 w-5 mr-3" />
                    KI-Nutzung
                  </NavLink>

                  <NavLink
                    to="/admin/user-activity"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <ChartBarSquareIcon className="h-5 w-5 mr-3" />
                    Nutzungsanalyse
                  </NavLink>
                </div>
              </div>
            )}

            {/* Admin Section - Only for Board */}
            {showAdminSection && (
              <div className="pt-1">
                <button
                  onClick={() => setAdminOpen(!adminOpen)}
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 mr-3" />
                    Admin
                  </div>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${
                      adminOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {adminOpen && (
                  <div className="space-y-1 pl-11">
                    <NavLink
                      to="/admin/config"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <CogIcon className="h-5 w-5 mr-3" />
                      Einstellungen
                    </NavLink>

                    <NavLink
                      to="/admin/structure"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <BuildingOfficeIcon className="h-5 w-5 mr-3" />
                      Struktur
                    </NavLink>

                    <NavLink
                      to="/admin/kassenpruefer"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <ShieldCheckIcon className="h-5 w-5 mr-3" />
                      Kassenprüfer-Verwaltung
                    </NavLink>

                    <NavLink
                      to="/admin/email-protokoll"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <EnvelopeIcon className="h-5 w-5 mr-3" />
                      E-Mail-Protokoll
                    </NavLink>

                    <NavLink
                      to="/admin/backup-export"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <CloudArrowDownIcon className="h-5 w-5 mr-3" />
                      Backup & Export
                    </NavLink>

                    <NavLink
                      to="/admin/backup-status"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <ArchiveBoxIcon className="h-5 w-5 mr-3" />
                      Backup-Status
                    </NavLink>

                    <NavLink
                      to="/admin/components-demo"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <SparklesIcon className="h-5 w-5 mr-3" />
                      Design System Demo
                    </NavLink>
                  </div>
                )}
              </div>
            )}
            </div>
          </nav>

          {/* User Section */}
          <div className="border-t border-gray-200/80 dark:border-gray-700 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-white/70 dark:bg-gray-800/30">
            <button
              onClick={() => navigate('/profile')}
              className="w-full flex items-center mb-2 p-2.5 rounded-xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-700 transition-colors cursor-pointer"
            >
              <span className="h-9 w-9 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                <UserCircleIcon className="h-5 w-5 text-primary-700 dark:text-primary-400" />
              </span>
              <div className="ml-3 text-left flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.vorname} {user?.nachname}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {getRolesDisplay(user)}
                </p>
              </div>
            </button>
            <div className="flex gap-2">
              <button
                onClick={toggleDarkMode}
                className="flex-1 flex items-center justify-center min-h-[40px] text-sm rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={darkMode ? 'Light Mode' : 'Dark Mode'}
              >
                {darkMode ? (
                  <SunIcon className="h-5 w-5" />
                ) : (
                  <MoonIcon className="h-5 w-5" />
                )}
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 flex items-center justify-center min-h-[40px] text-sm rounded-xl text-gray-500 dark:text-gray-400 hover:bg-error-50 hover:text-error-700 dark:hover:bg-error-900/20 dark:hover:text-error-400 transition-colors"
                title="Abmelden"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen min-w-0 flex flex-col lg:pl-72">
        {/* Top Bar */}
        <header className="app-topbar sticky top-0 z-30 flex h-14 sm:h-16 items-center bg-white/95 dark:bg-surface-dark/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 px-3 sm:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden h-10 w-10 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white mr-2 transition-colors"
            aria-label="Navigation öffnen"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2.5 min-w-0 lg:hidden">
            <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-primary-500 ring-1 ring-primary-600/20">
              <img src="/HSV_Logo.jpg" alt="HSV Berlin-Adlershof" className="h-full w-full object-cover" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-bold text-gray-900 dark:text-white leading-tight">
                {currentPage.title}
              </h2>
              {currentPage.subtitle && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 -mt-0.5">
                  {currentPage.subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right side: Präsenz + Rollen-Debug-Umschalter + Board Notes */}
          <div className="ml-auto flex items-center gap-2">
            <PraesenzAnzeige />
            {/* Rollen-Debug-Umschalter (nur bei REACT_APP_ROLE_DEBUG=true) */}
            {roleDebugEnabled && (
              <div className="hidden items-center gap-1.5 sm:flex" title="Rollen-Ansicht umschalten (nur Debug-Modus)">
                <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                  Debug-Rolle
                </span>
                <select
                  value={debugRole || ''}
                  onChange={(e) => setDebugRole(e.target.value || null)}
                  className="h-9 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 text-xs font-medium text-amber-800 dark:text-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer"
                  aria-label="Rollen-Ansicht umschalten"
                >
                  <option value="">Echte Rolle</option>
                  <option value="guest">Gast</option>
                  <option value="member">Mitglied</option>
                  <option value="trainer">Trainer</option>
                  <option value="board">Vorstand</option>
                </select>
              </div>
            )}

            {/* Board Notes Button */}
            {isBoardMember && (
              <button
                onClick={() => setNotesOpen(true)}
                className="relative h-10 w-10 flex items-center justify-center rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Vorstand-Nachrichten"
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                {unreadNotesCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-error-500 text-white text-[10px] font-bold">
                    {unreadNotesCount > 99 ? '99+' : unreadNotesCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="app-content min-w-0 flex-1 overflow-x-clip p-4 sm:p-6 lg:p-8">
          <div className="mx-auto min-w-0 w-full max-w-7xl">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-600 dark:text-gray-400">
                  © {new Date().getFullYear()} {clubName}
                </span>
                <span className="hidden sm:inline text-gray-400 dark:text-gray-600">•</span>
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded font-mono text-gray-500 dark:text-gray-400">
                  v{packageJson.version}{backendVersion ? ` / BE v${backendVersion}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  Bereitgestellt von{' '}
                  <a
                    href="https://mein-hundesportverein.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors font-medium"
                  >
                    mein-hundesportverein.de
                  </a>
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Board Notes Panel */}
      {isBoardMember && (
        <BoardNotesPanel
          isOpen={notesOpen}
          onClose={() => setNotesOpen(false)}
          pageContext={currentPage.title}
          onUnreadCountChange={setUnreadNotesCount}
        />
      )}
    </div>
  );
};

export default Layout;
